/**
 * completion.js
 *
 * Pure, dependency-free predicates for the premium narrative-completion ending.
 * Single-sourced here so gameState (the store), App.jsx (orchestration), and the
 * deterministic test (premium-ending.check.mjs) all agree on the same rules.
 *
 * The premium interview ends on NARRATIVE COMPLETION instead of a turn cap. A
 * case thread only counts once it has been "closed": its BIO-SCAN topic is
 * pressed on Hargrove WHILE its confirming evidence is already in the locker.
 * That enforces the gather-then-press loop the design calls for ("press the
 * threads after you have the evidence") rather than just checking both sets
 * exist in any order. celeste_grief is the optional emotional thread and never
 * gates completion.
 */

// The three case threads, as BIO-SCAN topic ids tracked in exhaustion.exploredTopics.
export const THREAD_TOPICS = ['courier_manifest', 'chip_donation', 'clinical_trial_st_erasmus'];

// The three thread-confirming evidence ids discovered at the lobby/office
// (dsl-donation, dso-manifest, dso-terminal). Stored in gameState.evidence.
export const THREAD_EVIDENCE = ['COURIER_MANIFEST', 'CHIP_DONATION_TIMING', 'ST_ERASMUS_ROUTING'];

// Which evidence item confirms each thread. A thread can only be pressed "with
// evidence" once its item is held.
export const THREAD_EVIDENCE_BY_TOPIC = {
  courier_manifest: 'COURIER_MANIFEST',
  chip_donation: 'CHIP_DONATION_TIMING',
  clinical_trial_st_erasmus: 'ST_ERASMUS_ROUTING',
};

// Craine-exhaustion threshold for the Night Owl debrief: after this many player
// turns, Craine closes the demo with the pie line.
export const NIGHT_OWL_MAX_TURNS = 10;

// Human-readable thread names for the evidence-leverage / pressure-calibration
// prompt steering.
export const THREAD_LABELS = {
  courier_manifest: 'the courier manifest',
  chip_donation: 'the chip donation timing',
  clinical_trial_st_erasmus: 'the St. Erasmus / clinical-trial routing',
};

/**
 * Which threads Craine currently has documentary evidence for (its confirming
 * item is in the locker). Drives the interview behavior steering.
 * @param {string[]} evidence
 * @returns {string[]} thread topic ids
 */
export function evidencedThreads(evidence) {
  const ev = Array.isArray(evidence) ? evidence : [];
  return THREAD_TOPICS.filter((t) => ev.includes(THREAD_EVIDENCE_BY_TOPIC[t]));
}

/**
 * Pre-formatted EVIDENCE LEVERAGE block for the Craine routing prompt. Built
 * client-side and interpolated as-is server-side (same byte-parity pattern as
 * the discovered-evidence block). Empty string when Craine holds no thread
 * evidence, so it costs nothing until the gather-then-press loop is underway.
 * @param {string[]} evidence
 * @returns {string}
 */
export function buildEvidenceLeverage(evidence) {
  const threads = evidencedThreads(evidence);
  if (!threads.length) return '';
  const list = threads.map((t) => THREAD_LABELS[t] || t).join(', ');
  return (
    `EVIDENCE LEVERAGE — you are holding documentary proof on these threads: ${list}.\n` +
    `When the chip's transmission points at one of these, press it with the quiet\n` +
    `confidence of a man who has the paper in his pocket: be specific, name the\n` +
    `discrepancy, and don't accept a vague brush-off. You still don't lay the file\n` +
    `on the table or reveal sources; you let him feel you already know. On threads\n` +
    `you do NOT have proof for, stay probing but lighter.\n`
  );
}

/**
 * Close a thread the moment it is pressed (its topic explored) while its
 * confirming evidence is already collected. Pure: returns the updated closed-set
 * (the SAME array reference when nothing changes, so callers can no-op cheaply).
 *
 * @param {string[]} prevClosed  threads already closed
 * @param {string} topic         the BIO-SCAN topic just pressed
 * @param {string[]} evidence    evidence ids currently held
 * @returns {string[]}
 */
export function closeThread(prevClosed, topic, evidence) {
  const closed = Array.isArray(prevClosed) ? prevClosed : [];
  const evId = THREAD_EVIDENCE_BY_TOPIC[topic];
  if (!evId) return closed;                       // not one of the three threads
  if (closed.includes(topic)) return closed;      // already closed
  if (!(Array.isArray(evidence) && evidence.includes(evId))) return closed; // evidence not yet held
  return [...closed, topic];
}

/**
 * True once all three threads have been closed (each pressed after its evidence
 * was collected). Pure function of the closed-set, so it is trivially testable.
 * @param {string[]} threadsClosed
 * @returns {boolean}
 */
export function isInterviewComplete(threadsClosed) {
  const closed = Array.isArray(threadsClosed) ? threadsClosed : [];
  return THREAD_TOPICS.every((t) => closed.includes(t));
}
